<template>
    <BaseDialog v-model:open="isOpen" show-close title="Upload your team" dialog-content-class="max-w-2xl!">
        <div class="p-4 md:p-10 pt-0! flex flex-col gap-6">
            <!-- Step 1: Upload -->
            <template v-if="step === 'upload'">
                <p class="body-small text-gray-600">
                    Upload a CSV of the team members' work emails. Only the <strong>email</strong> column is required. The team's lead sees the members' assessment results — that's
                    you, unless you designate someone else in the next step.
                </p>

                <!-- Mobile: simple upload button -->
                <div v-if="!selectedFile" class="md:hidden">
                    <button
                        type="button"
                        class="w-full flex items-center justify-center gap-2 px-4 py-3.5 border-2 border-dashed border-valence-grey-300 rounded-xl body-small text-gray-600 hover:border-valence-grey-750 active:bg-gray-50 transition-colors"
                        @click="triggerFileInput">
                        <i class="bi bi-file-earmark-arrow-up text-lg" aria-hidden="true"></i>
                        Upload CSV file
                    </button>
                </div>
                <!-- Desktop: drag-and-drop zone -->
                <div v-if="!selectedFile" class="hidden md:block">
                    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
                    <div
                        :class="{ 'border-valence-grey-750 bg-gray-50': isDragging }"
                        class="border-2 border-dashed border-valence-grey-300 rounded-xl p-8 text-center cursor-pointer hover:border-valence-grey-750 transition-colors"
                        role="button"
                        tabindex="0"
                        @click="triggerFileInput"
                        @keydown.enter="triggerFileInput"
                        @dragover.prevent="isDragging = true"
                        @dragleave="isDragging = false"
                        @drop.prevent="handleDrop">
                        <i class="bi bi-cloud-arrow-up text-3xl text-gray-400 mb-2 block" aria-hidden="true"></i>
                        <p class="body-small text-gray-500">Click to browse or drag and drop a CSV file</p>
                        <p class="details text-gray-400 mt-1">CSV files only, up to 1 MB</p>
                    </div>
                </div>

                <!-- File info card -->
                <div v-if="selectedFile" class="flex flex-col">
                    <div class="border border-gray-200 rounded-xl p-4 flex items-center gap-3">
                        <div class="rounded-full border-2 border-gray-200 min-w-10 min-h-10 flex items-center justify-center details uppercase text-gray-500">CSV</div>
                        <div class="grow min-w-0">
                            <div class="body-small-semibold truncate">{{ selectedFile.name }}</div>
                        </div>
                        <button
                            type="button"
                            aria-label="Remove selected file"
                            class="border border-gray-200 rounded-full flex items-center justify-center w-7 h-7 hover:bg-gray-50 transition-colors"
                            @click="clearFile">
                            <i class="bi bi-x text-gray-500 leading-none" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>

                <input ref="fileInput" type="file" accept=".csv" class="hidden" aria-label="CSV file upload" @change="handleFileSelect" />

                <div v-if="uploadError" class="body-small text-red-600">{{ uploadError }}</div>

                <button type="button" class="body-small text-[#0066CC] hover:text-[#0052A3] inline-flex items-center gap-1 w-fit" @click="downloadTemplate">
                    <i class="bi bi-download" aria-hidden="true"></i>
                    Download CSV template
                </button>

                <div class="flex justify-end gap-3 sticky bottom-0 bg-white pt-3 pb-4 md:relative md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 border-t border-gray-100 md:border-t-0">
                    <BaseButton theme="tertiary" @click="isOpen = false">Cancel</BaseButton>
                    <BaseButton theme="primary" :disabled="!selectedFile || isUploading" @click="handleUpload">
                        {{ isUploading ? "Uploading..." : "Preview Import" }}
                    </BaseButton>
                </div>
            </template>

            <!-- Step 2: Preview -->
            <template v-else-if="step === 'preview' && previewData">
                <div>
                    <h3 class="body-bold text-gray-900">Review your team</h3>
                    <p class="body-small text-gray-500 mt-1">Emails that don't match a user in your organization will be skipped.</p>
                </div>

                <label for="team-name-input" class="flex flex-col gap-1">
                    <span class="body-small-semibold text-gray-900">Team name</span>
                    <input
                        id="team-name-input"
                        v-model="teamName"
                        type="text"
                        maxlength="255"
                        placeholder="e.g. Platform Engineering"
                        class="body-small border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-valence-blue-600" />
                </label>

                <label for="lead-email-input" class="flex flex-col gap-1">
                    <span class="body-small-semibold text-gray-900">Team lead's email</span>
                    <input
                        id="lead-email-input"
                        v-model="leadEmail"
                        type="email"
                        maxlength="254"
                        placeholder="Leave empty if that's you"
                        class="body-small border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-valence-blue-600" />
                    <span class="details text-gray-500"
                        >Only the lead can see the team's assessment results. If you designate someone else, you'll manage the team without seeing results.</span
                    >
                </label>

                <div class="flex flex-wrap gap-3 body-small text-gray-600">
                    <span class="px-2 py-1 rounded bg-green-50 text-green-700">{{ previewData.summary.resolved }} will be added</span>
                    <span v-if="previewData.summary.unresolved > 0" class="px-2 py-1 rounded bg-amber-50 text-amber-700">{{ previewData.summary.unresolved }} not found</span>
                    <span v-if="previewData.summary.errors > 0" class="px-2 py-1 rounded bg-red-50 text-red-700">{{ previewData.summary.errors }} errors</span>
                </div>

                <div class="max-h-80 overflow-y-auto border border-valence-grey-300 rounded-lg">
                    <table class="w-full body-small">
                        <thead class="bg-gray-50 sticky top-0">
                            <tr>
                                <th class="text-left p-2 font-medium text-gray-400 w-8">#</th>
                                <th class="text-left p-2 font-medium">Email</th>
                                <th class="text-left p-2 font-medium">Name</th>
                                <th class="text-left p-2 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in previewData.rows" :key="row.row_index" :class="{ 'bg-red-50': !row.resolved }">
                                <td class="p-2 text-gray-400">{{ row.row_index + 1 }}</td>
                                <td class="p-2">{{ row.email || "(empty)" }}</td>
                                <td class="p-2 text-gray-500">{{ row.user_name || row.name || "-" }}</td>
                                <td class="p-2">
                                    <span v-if="row.resolved" class="text-green-600">Will be added</span>
                                    <span v-else class="text-red-600">{{ row.errors?.[0] || "Will be skipped" }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-end gap-3 sticky bottom-0 bg-white pt-3 pb-4 md:relative md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 border-t border-gray-100 md:border-t-0">
                    <BaseButton theme="tertiary" @click="resetToUpload">Back</BaseButton>
                    <BaseButton theme="primary" :disabled="isImporting || !hasImportableRows" @click="handleImport">
                        {{ isImporting ? "Importing..." : "Confirm Import" }}
                    </BaseButton>
                </div>
            </template>

            <!-- Step 3: Importing -->
            <template v-else-if="step === 'importing'">
                <div class="text-center py-8">
                    <div class="overflow-hidden mb-3"><i class="bi bi-arrow-repeat text-3xl text-gray-400 block animate-spin" aria-hidden="true"></i></div>
                    <h3 class="title-body mb-2">Importing...</h3>
                    <template v-if="importProgress">
                        <p class="body-small text-gray-500 mb-4">{{ importProgress.processed }} of {{ importProgress.total }} rows processed</p>
                        <BaseProgressBar :value="progressPercent" :aria-label="`${importProgress.processed} of ${importProgress.total} rows processed`" />
                    </template>
                </div>
            </template>

            <!-- Step 4: Result -->
            <template v-else-if="step === 'result' && importResult">
                <div class="text-center py-4">
                    <i class="bi bi-check-circle text-4xl text-green-500 mb-3 block" aria-hidden="true"></i>
                    <h3 class="title-body mb-2">Team updated</h3>
                    <div class="flex flex-wrap justify-center gap-3 body-small text-gray-600 mb-4">
                        <span v-if="importResult.created" class="px-2 py-1 rounded bg-green-50 text-green-700">{{ importResult.created }} added</span>
                        <span v-if="importResult.skipped" class="px-2 py-1 rounded bg-gray-100 text-gray-600">{{ importResult.skipped }} skipped</span>
                        <span v-if="importResult.errors?.length" class="px-2 py-1 rounded bg-red-50 text-red-700">{{ importResult.errors?.length }} not found</span>
                    </div>
                    <details v-if="importResult.errors?.length" class="text-left mb-4 mx-auto max-w-md">
                        <summary class="body-small text-red-600 cursor-pointer hover:text-red-700">Show details</summary>
                        <ul class="mt-2 space-y-1 body-small text-gray-600">
                            <li v-for="(rowError, idx) in importResult.errors ?? []" :key="idx" class="px-3 py-1.5 bg-red-50 rounded text-red-700">
                                {{ rowError.error }}
                            </li>
                        </ul>
                    </details>
                    <BaseButton theme="primary" @click="handleDone">Done</BaseButton>
                </div>
            </template>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useIntervalFn } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import type { CsvImportStatusResponse, TeamCsvPreviewResponse } from "~vue/api/generated/teams-csv";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import { computed, ref, watch } from "vue";

const TEMPLATE_URL = "/v1/teams/csv/template";
const PREVIEW_URL = "/v1/teams/csv/preview";
const IMPORT_URL = "/v1/teams/csv/import";
const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

const props = defineProps<{ initialTeamName?: string }>();
const emit = defineEmits<{ imported: [CsvImportStatusResponse] }>();
const isOpen = defineModel<boolean>("open", { default: false });

const step = ref<"upload" | "preview" | "importing" | "result">("upload");
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const uploadError = ref("");
const fileInput = ref<HTMLInputElement | null>(null);
const previewData = ref<TeamCsvPreviewResponse | null>(null);
const teamName = ref(props.initialTeamName ?? "");
const leadEmail = ref("");
const importResult = ref<CsvImportStatusResponse | null>(null);
const isUploading = ref(false);
const isImporting = ref(false);
const importProgress = ref<{ processed: number; total: number } | null>(null);
const pollStatusUrl = ref<string | null>(null);
const pollStartTime = ref(0);

const hasImportableRows = computed(() => previewData.value?.rows.some((row) => row.resolved) ?? false);

const progressPercent = computed(() => {
    if (!importProgress.value?.total) {
        return 0;
    }
    return Math.round((importProgress.value.processed / importProgress.value.total) * 100);
});

const { pause: pausePoll, resume: resumePoll } = useIntervalFn(
    async () => {
        if (!pollStatusUrl.value) {
            return;
        }
        if (Date.now() - pollStartTime.value > POLL_TIMEOUT_MS) {
            pausePoll();
            uploadError.value = "Import timed out. Please try again.";
            step.value = "preview";
            return;
        }
        const { data } = await useFetch(pollStatusUrl.value).get().json<CsvImportStatusResponse>();
        if (!data.value) {
            return;
        }
        importProgress.value = { processed: data.value.processed ?? 0, total: data.value.total ?? 0 };
        if (data.value.status === "completed") {
            pausePoll();
            importResult.value = data.value;
            step.value = "result";
        } else if (data.value.status === "failed") {
            pausePoll();
            uploadError.value = data.value.error_message || "Import failed. Please try again.";
            step.value = "preview";
        }
    },
    POLL_INTERVAL_MS,
    { immediate: false },
);

const triggerFileInput = () => {
    fileInput.value?.click();
};

const downloadTemplate = async () => {
    const response = await fetchApi(TEMPLATE_URL);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "team_members_template.csv";
    a.click();
    URL.revokeObjectURL(url);
};

const setFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
        uploadError.value = "File size exceeds 1 MB limit.";
        return;
    }
    selectedFile.value = file;
    uploadError.value = "";
};

const handleFileSelect = (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
        setFile(file);
    }
};

const handleDrop = (event: DragEvent) => {
    isDragging.value = false;
    const file = event.dataTransfer?.files?.[0];
    if (file && file.name.endsWith(".csv")) {
        setFile(file);
    } else {
        uploadError.value = "Please drop a CSV file.";
    }
};

const clearFile = () => {
    selectedFile.value = null;
    uploadError.value = "";
    if (fileInput.value) {
        fileInput.value.value = "";
    }
};

const handleUpload = async () => {
    if (!selectedFile.value) {
        return;
    }
    isUploading.value = true;
    uploadError.value = "";

    const formData = new FormData();
    formData.append("file", selectedFile.value);

    const { data, error } = await useFetch(PREVIEW_URL).post(formData).json<TeamCsvPreviewResponse>();
    isUploading.value = false;

    if (error.value || !data.value) {
        uploadError.value = error.value?.message || "Failed to process CSV file. Please try again.";
        return;
    }
    previewData.value = data.value;
    step.value = "preview";
};

const handleImport = async () => {
    if (!previewData.value) {
        return;
    }
    isImporting.value = true;

    const rows = previewData.value.rows.filter((row) => row.resolved).map((row) => ({ row_index: row.row_index, email: row.email, name: row.name, skip: false }));

    const { data, error } = await useFetch(IMPORT_URL).post({ team_name: teamName.value, lead_email: leadEmail.value.trim(), rows }).json<{ status_url: string }>();
    isImporting.value = false;

    if (error.value || !data.value?.status_url) {
        uploadError.value = error.value?.message || "Import failed. Please try again.";
        return;
    }

    pollStatusUrl.value = data.value.status_url;
    importProgress.value = { processed: 0, total: rows.length };
    pollStartTime.value = Date.now();
    step.value = "importing";
    resumePoll();
};

const resetToUpload = () => {
    step.value = "upload";
    previewData.value = null;
};

const handleDone = () => {
    isOpen.value = false;
    if (importResult.value) {
        emit("imported", importResult.value);
    }
};

watch(isOpen, (newVal) => {
    if (!newVal) {
        pausePoll();
        step.value = "upload";
        selectedFile.value = null;
        uploadError.value = "";
        previewData.value = null;
        leadEmail.value = "";
        importResult.value = null;
        isUploading.value = false;
        isImporting.value = false;
        isDragging.value = false;
        importProgress.value = null;
        pollStatusUrl.value = null;
    }
});
</script>
